import {
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from "react-query";
import { GATEWAY_URL } from "./constants";
import useFetchWithAuth from "../fetchWithAuth";
import useGroupQuery from "./useGroupQuery";
import { Post } from "@/fetch/social";
import {
  formatPostsData,
  Media,
  SampleLike,
  SampleComment,
} from "./useRecentPostsQuery";

const PAGE_SIZE = 10;

type TravellerPostsVariables = {
  cid: number | undefined;
};

type TravellerPostsParam = {
  pageSize: number;
  lastId?: number;
  lastCa?: number;
};

type TravellerPostsQueryResult = {
  posts: Array<Post>;
  media: { [postId: string | number]: Array<Media> };
  likes: {
    [postId: string | number]: {
      totalLikes: number;
      sampleLikes: SampleLike[];
    };
  };
  comments: {
    [postId: string | number]: {
      totalComments: number;
      sampleComments: SampleComment[];
    };
  };
  travellers: {
    [cid: number | string]: {
      cid: number;
      nickname: string;
      email: string;
      country: string;
      division: string;
      trip_status: string;
      s3_profile_pic: string;
    };
  };
};

export type RecentPostsFormattedResult = Array<Post>;

type TravellerPostsFetchResult = {
  data: TravellerPostsQueryResult;
  statusCode: number;
  success: boolean | string;
};

export type InfinityResult = {
  pages: Array<Array<Post>>;
  pageParam: any[];
};
type QueryError = {};

const useTravellerPostsQuery = (
  variables: TravellerPostsVariables,
  params: TravellerPostsParam = { pageSize: PAGE_SIZE },
  options?: UseInfiniteQueryOptions<
    RecentPostsFormattedResult,
    any,
    RecentPostsFormattedResult,
    RecentPostsFormattedResult,
    QueryKey
  >
) => {
  const { data: group, isLoading: isGroupLoading } = useGroupQuery();
  const { fetchWithAuth } = useFetchWithAuth();
  const groupId = group?.id;
  const cid = variables?.cid;

  const url = `${GATEWAY_URL}/v2/traveller/${cid}/posts/group/${groupId}`;
  const cacheKey = [url];

  const queryResult = useInfiniteQuery<RecentPostsFormattedResult, QueryError>(
    cacheKey,
    async ({ pageParam }) => {
      if (!groupId || !cid) throw new Error("Group id or cid not found");

      const result = await fetchWithAuth<TravellerPostsFetchResult>(url, {
        params: {
          ...params,
          pageSize: params?.pageSize ?? PAGE_SIZE,
          ...(pageParam ?? {}),
        },
      });
      const formattedPosts = formatPostsData(result.data);
      return formattedPosts;
    },
    {
      ...options,
      enabled: options?.enabled ?? Boolean(groupId && cid),
      getNextPageParam: (lastPage, pages) => {
        if (!lastPage.length) {
          return undefined;
        }

        const lastPost = lastPage[lastPage.length - 1];

        return {
          lastId: lastPost.id,
          lastCa: lastPost._ca,
        };
      },
    }
  );
  return { ...queryResult, isLoading: queryResult.isLoading || isGroupLoading };
};
export default useTravellerPostsQuery;
